import React, { useState } from "react";

const inputClass = "w-full px-4 py-2.5 rounded-lg bg-white/5 border border-navy-border text-white focus:outline-none focus:border-accent-light";

const emptyJob = {
  title: "", description: "", category: "", budget_type: "fixed", budget_min: "", budget_max: "", skills: "", deadline: "",
};

// Create / edit a job. onSubmit(payload) resolves to an error string, or "" on success.
export default function JobForm({ settings, initial, onSubmit, onCancel }) {
  const [form, setForm] = useState(initial ? { ...emptyJob, ...initial } : emptyJob);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const categories = (settings?.categories || "").split("\n").map((c) => c.trim()).filter(Boolean);
  const currency = settings?.currency_label || "USD";
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const payload = {
      title: form.title, description: form.description, category: form.category, budget_type: form.budget_type,
      budget_min: form.budget_min === "" ? null : form.budget_min,
      budget_max: form.budget_max === "" ? null : form.budget_max,
      skills: form.skills, deadline: form.deadline || null,
    };
    const message = await onSubmit(payload);
    setBusy(false);
    if (message) setError(message);
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label className="block text-sm text-gray-300 mb-1">Job title</label>
        <input required maxLength={150} className={inputClass} value={form.title} onChange={set("title")} placeholder="e.g. Build a responsive company website" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-300 mb-1">Category</label>
          <select className={`${inputClass} bg-navy-dark`} value={form.category} onChange={set("category")}>
            <option value="">Select a category</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-300 mb-1">Payment type</label>
          <select className={`${inputClass} bg-navy-dark`} value={form.budget_type} onChange={set("budget_type")}>
            <option value="fixed">Fixed price</option>
            <option value="hourly">Hourly rate</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-300 mb-1">Budget from ({currency})</label>
          <input type="number" min="0" step="0.01" className={inputClass} value={form.budget_min ?? ""} onChange={set("budget_min")} />
        </div>
        <div>
          <label className="block text-sm text-gray-300 mb-1">Budget to ({currency})</label>
          <input type="number" min="0" step="0.01" className={inputClass} value={form.budget_max ?? ""} onChange={set("budget_max")} />
        </div>
      </div>
      <div>
        <label className="block text-sm text-gray-300 mb-1">Description</label>
        <textarea required rows={6} className={inputClass} value={form.description} onChange={set("description")} placeholder="Describe the work, deliverables and anything sellers should know..." />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-300 mb-1">Skills (comma separated)</label>
          <input className={inputClass} value={form.skills} onChange={set("skills")} placeholder="React, Node.js, MySQL" />
        </div>
        <div>
          <label className="block text-sm text-gray-300 mb-1">Deadline (optional)</label>
          <input type="date" className={inputClass} value={form.deadline || ""} onChange={set("deadline")} />
        </div>
      </div>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <div className="flex gap-3">
        <button disabled={busy} className="px-6 py-2.5 rounded-full bg-brand-gradient font-semibold disabled:opacity-60">
          {busy ? "Saving..." : initial ? "Save Changes" : "Post Job"}
        </button>
        {onCancel && <button type="button" onClick={onCancel} className="px-6 py-2.5 rounded-full border border-navy-border text-gray-300 hover:text-white">Cancel</button>}
      </div>
    </form>
  );
}
